import { useState } from "react";
import { toast } from "sonner";
import { Copy, Check } from "lucide-react";
import { CandyButton } from "../playful/CandyButton";
import { PopInput } from "../playful/PopInput";
import { GeometricBadge } from "../playful/GeometricBadge";
import { StickerCard } from "../playful/StickerCard";
import { ConfettiDecoration } from "../playful/Decor";
import { api, formatApiError } from "../../lib/api";

/** LiveShortener — working "try it now" demo. Self-contained (own state + API). */
export default function LiveShortener() {
  const [url, setUrl] = useState("");
  const [alias, setAlias] = useState("");
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const onShorten = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) {
      toast.error("Paste a URL first.");
      return;
    }
    setBusy(true);
    try {
      const payload = { url };
      if (alias) payload.custom_alias = alias;
      const { data } = await api.post("/links", payload);
      setLink(data);
      toast.success("Lane opened.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.short_url);
      setCopied(true);
      toast.success("Copied.");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Could not copy.");
    }
  };

  return (
    <StickerCard
      tone="candy"
      bg="paper"
      hover={false}
      rounded="3xl"
      className="relative overflow-hidden p-6 md:p-8"
    >
      <ConfettiDecoration className="opacity-30" density="sparse" />
      <div className="relative">
        <div className="mb-4 flex items-center gap-2">
          <GeometricBadge tone="candy" size="sm">
            TRY IT NOW
          </GeometricBadge>
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            — no account needed
          </span>
        </div>
        <form onSubmit={onShorten} className="grid grid-cols-12 gap-4">
          <div className="col-span-12 md:col-span-8">
            <PopInput
              label="Paste any long URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-very-long-link.example.com/article/with/path"
              data-testid="hero-url-input"
            />
          </div>
          <div className="col-span-8 md:col-span-3">
            <PopInput
              label="Custom alias"
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="my-link"
              data-testid="hero-alias-input"
            />
          </div>
          <div className="col-span-4 flex items-end md:col-span-1">
            <CandyButton
              type="submit"
              variant="candy"
              size="md"
              disabled={busy}
              withArrow
              block
              data-testid="hero-shorten-btn"
            >
              {busy ? "…" : "Go"}
            </CandyButton>
          </div>
        </form>

        {link ? (
          <div
            className="pop-in mt-5 flex flex-wrap items-center gap-4 rounded-2xl border-2 border-ink bg-mint-tint px-5 py-4"
            data-testid="hero-result"
          >
            <GeometricBadge tone="mint" size="sm">
              LANE OPENED
            </GeometricBadge>
            <a
              href={link.short_url}
              target="_blank"
              rel="noreferrer"
              className="break-all font-display text-lg font-extrabold text-candy underline-offset-4 hover:underline md:text-2xl"
              data-testid="hero-result-url"
            >
              {link.short_url}
            </a>
            <div className="ml-auto flex flex-wrap gap-2">
              <CandyButton
                onClick={copy}
                variant={copied ? "mint" : "candy"}
                size="sm"
                data-testid="hero-copy-btn"
              >
                {copied ? (
                  <>
                    <Check size={13} strokeWidth={3} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={13} strokeWidth={3} /> Copy
                  </>
                )}
              </CandyButton>
              <CandyButton to="/signup" variant="yellow" size="sm">
                Save to dashboard
              </CandyButton>
            </div>
          </div>
        ) : null}
      </div>
    </StickerCard>
  );
}
